import { useEffect, useState } from "react";
import { Groupper, Input, Button, Field, Message, Table } from "react-frontier";
import { Dropdown } from "semantic-ui-react";
import { useBreadcrumbs } from "../hooks";
import { Department, ExpenseType, ExpensesReportSummary } from "../SiteClasses";
import { addCommas } from "../SiteUtil";
import Validator from "../Validator";
import moment from "moment";
import API from "../API";

enum ExpenseStatus{
	PENDING = 1,
	APPROVED = 2,
	PAID = 3,
	REJECTED = 4,
}

var ExpensesReport = ()=>{
	var { setTitle } = useBreadcrumbs();
	var [startDate, setStartDate] = useState<number>(moment().subtract(7, 'd').unix());
	var [endDate, setEndDate] = useState<number>(moment().unix());
	var [departments, setDepartments] = useState<number[]>([]);
	var [statuses, setStatuses] = useState<number[]>([ExpenseStatus.PENDING, ExpenseStatus.APPROVED, ExpenseStatus.PAID, ExpenseStatus.REJECTED]);
	var [types, setTypes] = useState<number[]>([]);
	var [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	var [errorPrompts, setErrorPrompts] = useState<string[]>([]);
	var [summary, setSummary] = useState<ExpensesReportSummary>(null);
	var [searched, setSearched] = useState<boolean>(false);

	useEffect(()=>{
		setTitle('Reporte de gastos');
		API.getExpenseTypes().then(res=>{
			if(res.error) return;
			setExpenseTypes(res.data);
		});
	}, [])

	var validateForm = ()=>{
		var { valid, prompts } = Validator({ startDate, endDate }, {
			startDate: [{ rule: 'number', label: 'Fecha inicio' }],
			endDate: [{ rule: 'number', label: 'Fecha fin' }]
		});
		setErrorPrompts(prompts);
		if(!valid) return null;
		if(endDate <= startDate){ setErrorPrompts(['La fecha de fin debe ser mayor que la fecha de inicio']); return null; }
		if(departments?.length === 0){ setErrorPrompts(['Selecciona al menos un departamento']); return null; }
		if(statuses?.length === 0){ setErrorPrompts(['Selecciona al menos un estatus']); return null; }
		if(types?.length === 0){ setErrorPrompts(['Selecciona al menos un tipo de gasto']); return null; }
		return {
			dateStart: moment.unix(startDate).startOf('day').unix(),
			dateEnd: moment.unix(endDate).endOf('day').unix(),
		};
	}

	var searchReport = async(setLoading: (l: boolean)=>void)=>{
		var params = validateForm();
		if(!params) return;

		setLoading(true);
		setSummary(null);
		setSearched(false);
		API.getExpensesReportSummary(params.dateStart, params.dateEnd, departments, statuses, types).then(res=>{
			setSearched(true);
			if(res.error) return setErrorPrompts([res.message]);
			if(!res.data || res.data.total_expenses === 0) return setErrorPrompts(['No se encontró información con los filtros seleccionados']);
			setErrorPrompts([]);
			setSummary(res.data);
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado (EXP-RPT-2)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var downloadReport = async(setLoading: (l: boolean)=>void)=>{
		var params = validateForm();
		if(!params) return;

		setLoading(true);
		API.downloadExpensesReport(params.dateStart, params.dateEnd, departments, statuses, types).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
		}).catch(err=>{
			setErrorPrompts(['Hubo un error inesperado (EXP-RPT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	return <div>
		<Groupper title={'Reporte de gastos'} width={500} style={{ marginTop: 20 }} actions={<>
				<Button text="Descargar" color="green" iconName="download" onClick={downloadReport} disabled={!summary} />
				<Button text="Buscar" color="black" onClick={searchReport} />
			</>
		}>
			<Field amount={2}>
				<Input 
					label='Fecha de inicio' 
					value={startDate} 
					onChange={setStartDate} 
					calendar={{ date: startDate, mode: 'date', format: 'DD/MM/YYYY' }} 
					style={{ width: '100%' }}
				/>
				<Input 
					label='Fecha de fin' 
					value={endDate} 
					onChange={setEndDate} 
					calendar={{ date: endDate, mode: 'date', format: 'DD/MM/YYYY' }} 
					style={{ width: '100%' }}
				/>
			</Field>
			<Field label='Departamento'>
				<Dropdown 
					selection 
					value={departments}
					multiple
					placeholder='Departamento' options={[
						{ value: Department.OPERACIONES, text: 'Operaciones' },
						{ value: Department.COMERCIAL, text: 'Comercial' },
						{ value: Department.DESARROLLO, text: 'Desarrollo' },
						{ value: Department.RECURSOS_HUMANOS, text: 'Recursos Humanos' },
						{ value: Department.MARKETING, text: 'Marketing' },
						{ value: Department.CONTABILIDAD, text: 'Contabilidad' },
					]} 
					onChange={(e, { value })=> setDepartments(value as number[])}
				/>
			</Field>
			<Field label='Tipo de gasto'>
				<Dropdown 
					selection 
					multiple
					value={types}
					placeholder='Tipo de gasto' options={expenseTypes.map(t => ({
						value: t.type_id,
						text: t.type_name,
					}))}
					onChange={(e, { value })=> setTypes(value as number[])}
				/>
			</Field>
			<Field label='Estatus'>
				<Dropdown 
					selection 
					multiple
					value={statuses}
					placeholder='Todos' options={[
						{ value: ExpenseStatus.PENDING, text: 'Pendiente' },
						{ value: ExpenseStatus.APPROVED, text: 'Aprobado' },
						{ value: ExpenseStatus.PAID, text: 'Pagado' },
						{ value: ExpenseStatus.REJECTED, text: 'Rechazado' },
					]} 
					onChange={(e, { value })=> setStatuses(value as number[])}
				/>
			</Field>
			<Message list={errorPrompts} type={'error'} />
		</Groupper>
		{summary && (
			<Table
				details
				striped
				title='Resumen'
				style={{ maxWidth: 500, margin: 'auto', marginTop: 15, marginBottom: 15 }}
				data={[
					['Total de gastos', summary.total_expenses],
					['Coordinadores', summary.total_coordinators],
					['Eventos', summary.total_events],
					['Departamentos', summary.total_departments],
					['Reposiciones', summary.total_replacements],
					null,
					['Pendientes', summary.total_pending],
					['Aprobados', summary.total_approved],
					['Pagados', summary.total_paid],
					['Rechazados', summary.total_rejected],
					null,
					['Total pendientes', '$' + addCommas(summary.amount_pending)],
					['Total aprobados', '$' + addCommas(summary.amount_approved)],
					['Total pagados', '$' + addCommas(summary.amount_paid)],
					['Total rechazados', '$' + addCommas(summary.amount_rejected)],
					[<b>Total general</b>, <b>{'$' + addCommas(summary.amount_total)}</b>],
				]}
			/>
		)}
	</div>
}

export default ExpensesReport;